/* eslint-disable react-hooks/exhaustive-deps */
import { useEbook } from '@orientaction/hooks';
import { getFromLS } from '@orientaction/utils';
import dateFormat from 'dateformat';
import { useEffect, useState } from 'react';
import { apiServiceNative } from '../../../utils/fetchNative';
import { toId, unwrapCollection } from '../shared/utils';
import { AssignmentResourceFilter, isAllowedId } from './assignmentVisibility';

export interface ResourceResultRow {
  id: string;
  name: string;
  assignedAt: string;
  consultedAt: string;
  isRead: boolean;
  viewPath?: string;
  openInNewTab?: boolean;
  actionLabel?: string;
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return dateFormat(date, 'dd/mm/yyyy, HH:MM');
};

const classicBookId = (book: any) => Number(book?.attributes?.id ?? book?.id) || 0;

const sortClassicBooks = (books: any[]) =>
  [...books].sort((a, b) => classicBookId(a) - classicBookId(b));

const normalizeEbookName = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const majorEbookRank = (name: string) => {
  const normalized = normalizeEbookName(name);
  if (normalized.includes('introduction')) {
    return 0;
  }
  const step = normalized.match(/etape\s*(\d+)/);
  if (step) {
    return Number(step[1]);
  }
  if (normalized.includes('recrutement')) {
    return 1000;
  }
  return 500;
};

const mapClassicEbook = (book: any): ResourceResultRow | null => {
  const id = toId(book);
  if (!id) {
    return null;
  }
  const attrs = book.attributes || book;
  const { route } = attrs;
  return {
    id: `classic-${id}`,
    name: String(attrs.name || attrs.title || `Ebook ${id}`),
    assignedAt: formatDateTime(attrs.DateAttribuate || attrs.dateAttribuate || attrs.createdAt),
    consultedAt: formatDateTime(attrs.dateConsultation),
    isRead: Boolean(attrs.dateConsultation),
    viewPath: route ? `/${route}/${id}/readOnly` : undefined,
  };
};

const mapRecrutementEbook = (item: any, beneficiaryId: number): ResourceResultRow | null => {
  const source = item?.attributes ? { id: item.id, ...item.attributes } : item;
  const id = toId(source);
  if (!id) {
    return null;
  }
  const consulted = source.dateConsultation || source.date_consultation;
  return {
    id: `recrutement-${id}`,
    name: 'Réussir son recrutement',
    assignedAt: formatDateTime(source.createdAt),
    consultedAt: formatDateTime(consulted),
    isRead: Boolean(consulted),
    viewPath: `/ebook-recrutement/${id}/${true}/0?userId=${beneficiaryId}`,
  };
};

function mapCustomEbook(ebook: any, beneficiaryId: number): ResourceResultRow | null {
  const ebookId = toId(ebook?.idEbook || ebook);
  const resultId = toId(ebook?.idResult);
  if (!ebookId && !resultId) {
    return null;
  }
  const consulted = ebook?.date_consultation || ebook?.dateConsultation;
  return {
    id: `custom-ebook-${resultId || ebookId}`,
    name: String(ebook?.title || ebook?.name || `Ebook ${ebookId}`),
    assignedAt: formatDateTime(ebook?.createdAt || ebook?.DateAttribuate || consulted),
    consultedAt: formatDateTime(consulted),
    isRead: Boolean(consulted),
    viewPath: resultId
      ? `/ebook/view/${resultId}/0?readOnly=true&userId=${beneficiaryId}`
      : undefined,
  };
}

const mapGame = (game: any, beneficiaryId: number): ResourceResultRow | null => {
  const gameId = toId(game?.idGame || game);
  const resultId = toId(game?.idResult);
  if (!gameId) {
    return null;
  }
  const consulted = game?.date_consultation || game?.dateConsultation;
  const canViewSynthesis = Boolean(game?.isCompletedSynthesis && resultId);
  return {
    id: `game-${resultId || gameId}`,
    name: String(game?.title || game?.name || `Serious game ${gameId}`),
    assignedAt: formatDateTime(consulted || game?.createdAt),
    consultedAt: formatDateTime(consulted),
    isRead: Boolean(consulted),
    viewPath: canViewSynthesis
      ? `/game/result/${resultId}?from=admin&userId=${beneficiaryId}`
      : undefined,
    actionLabel: canViewSynthesis ? 'Voir la synthèse' : undefined,
  };
};

export const useResourceResults = (
  user: any,
  assignmentFilter?: AssignmentResourceFilter | null,
  assignmentFilterReady = true
) => {
  const userId = Number(user?.id);
  const token = getFromLS('userToken') || '';
  const { getMyBookRequest } = useEbook({ mode: 'none', user: 'none' });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [ebookRows, setEbookRows] = useState<ResourceResultRow[]>([]);
  const [customEbookRows, setCustomEbookRows] = useState<ResourceResultRow[]>([]);
  const [gameRows, setGameRows] = useState<ResourceResultRow[]>([]);

  useEffect(() => {
    if (!userId || !token) {
      return undefined;
    }
    if (!assignmentFilterReady) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [classicRes, recrutementRes, customEbooksRes, gamesRes] = await Promise.all([
          getMyBookRequest('', 0, String(userId)).catch(() => null),
          apiServiceNative
            .get(
              `/api/ebook-recrutements?filters[users_permissions_user][id][$eq]=${userId}&populate=*&publicationState=preview`
            )
            .catch(() => null),
          apiServiceNative.get(`/api/ebook-list-user/${userId}`).catch(() => []),
          apiServiceNative.get(`/api/game-list-user/${userId}`).catch(() => []),
        ]);

        if (cancelled) {
          return;
        }

        const classic = sortClassicBooks(unwrapCollection(classicRes?.data?.list))
          .filter((book: any) => isAllowedId(assignmentFilter?.bookIds, book))
          .map(mapClassicEbook)
          .filter((row): row is ResourceResultRow => Boolean(row));
        const recrutement = unwrapCollection(recrutementRes?.data || recrutementRes)
          .filter((item: any) => {
            const source = item?.attributes ? { id: item.id, ...item.attributes } : item;
            return (
              isAllowedId(assignmentFilter?.bookIds, source) ||
              isAllowedId(assignmentFilter?.bookIds, source?.book_template)
            );
          })
          .map((item: any) => mapRecrutementEbook(item, userId))
          .filter((row): row is ResourceResultRow => Boolean(row));
        const customEbooks = unwrapCollection(customEbooksRes)
          .filter((ebook: any) => isAllowedId(assignmentFilter?.ebookIds, ebook?.idEbook || ebook))
          .map((ebook: any) => mapCustomEbook(ebook, userId))
          .filter((row): row is ResourceResultRow => Boolean(row));
        const games = unwrapCollection(gamesRes)
          .filter((game: any) => isAllowedId(assignmentFilter?.gameIds, game?.idGame || game))
          .map((game: any) => mapGame(game, userId))
          .filter((row): row is ResourceResultRow => Boolean(row));

        const ebookByName = new Map<string, ResourceResultRow>();
        [...classic, ...recrutement].forEach(row => {
          const key = row.name.trim().toLowerCase();
          if (!ebookByName.has(key)) {
            ebookByName.set(key, row);
          }
        });

        setEbookRows(
          Array.from(ebookByName.values()).sort(
            (a, b) => majorEbookRank(a.name) - majorEbookRank(b.name)
          )
        );
        setCustomEbookRows(customEbooks);
        setGameRows(games);
      } catch {
        if (!cancelled) {
          setEbookRows([]);
          setCustomEbookRows([]);
          setGameRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, token, assignmentFilter, assignmentFilterReady]);

  const term = search.trim().toLowerCase();
  const filterRows = (rows: ResourceResultRow[]) =>
    term ? rows.filter(row => row.name.toLowerCase().includes(term)) : rows;

  return {
    loading,
    search,
    setSearch,
    ebookRows: filterRows(ebookRows),
    customEbookRows: filterRows(customEbookRows),
    gameRows: filterRows(gameRows),
    hasAnyResult: ebookRows.length > 0 || customEbookRows.length > 0 || gameRows.length > 0,
  };
};
